// ...existing code...

import api from './axiosConfig';
import {
  Employee,
  EmployeeDirectoryItem,
  EmployeeProfile,
  CreateEmployeeQuick,
  BulkUploadEmployeeRow,
  BulkUploadEmployeesResponse,
  BulkUploadedEmployeeInfo,
  UpdateEmployeeBasicDetails,
  UpdateEmployeeJobDetails,
  UpdateEmployeeBankDetails,
  UpdateEmployeeContactDetails,
  UpdateEmployeeSupervisor,
  UpdateEmployeeSettings,
  UpdateEmployeeRoles,
  UpdateEmployeeEmailSubscriptions,
  UploadEmployeeDocument,
  AddEmergencyContact,
  UpdateEmergencyContact,
  AddDependent,
  UpdateDependent,
  AddSubscriberGroup,
  UpdateSubscriberGroup,
} from '../types';

export const employeeApi = {
    // ...existing code...
    updateDocument: async (docId: string, data: {
      documentName: string;
      documentType?: string;
      documentNumber?: string;
      issueDate?: string;
      expiryDate?: string;
      remarks?: string;
      filePath?: string;
      fileSize?: string;
    }) => {
      await api.put(`/employees/documents/${docId}`, data);
    },

    deleteDocument: async (docId: string): Promise<void> => {
      await api.delete(`/employees/documents/${docId}`);
    },
  getCurrentEmployee: async (): Promise<{ type: string; id: string; fullName: string; employeeCode: string; email: string; joinedDate?: string | null }> => {
    const response = await api.get('/employees/me');
    return response.data;
  },

  getAll: async (page = 1, pageSize = 10, searchTerm?: string, filters?: {
    departmentId?: string;
    designationId?: string;
    branchId?: string;
    employmentStatus?: number;
    joinedDateFrom?: string;
    joinedDateTo?: string;
  }, sortBy?: string, sortDirection?: 'asc' | 'desc'): Promise<Employee[]> => {
    const params: any = { pageNumber: page, pageSize };
    if (searchTerm) params.searchTerm = searchTerm;
    if (sortBy) params.sortBy = sortBy;
    if (sortDirection) params.sortDirection = sortDirection;
    if (filters?.departmentId) params.departmentId = filters.departmentId;
    if (filters?.designationId) params.designationId = filters.designationId;
    if (filters?.branchId) params.branchId = filters.branchId;
    if (filters?.employmentStatus !== undefined && filters?.employmentStatus !== null) params.employmentStatus = filters.employmentStatus;
    if (filters?.joinedDateFrom) params.joinedDateFrom = filters.joinedDateFrom;
    if (filters?.joinedDateTo) params.joinedDateTo = filters.joinedDateTo;
    const response = await api.get<Employee[]>('/employees', { params });
    return response.data;
  },

  getCount: async (searchTerm?: string, filters?: {
    departmentId?: string;
    designationId?: string;
    branchId?: string;
    employmentStatus?: number;
    joinedDateFrom?: string;
    joinedDateTo?: string;
  }): Promise<number> => {
    const params: any = {};
    if (searchTerm) params.searchTerm = searchTerm;
    if (filters?.departmentId) params.departmentId = filters.departmentId;
    if (filters?.designationId) params.designationId = filters.designationId;
    if (filters?.branchId) params.branchId = filters.branchId;
    if (filters?.employmentStatus !== undefined && filters?.employmentStatus !== null) params.employmentStatus = filters.employmentStatus;
    if (filters?.joinedDateFrom) params.joinedDateFrom = filters.joinedDateFrom;
    if (filters?.joinedDateTo) params.joinedDateTo = filters.joinedDateTo;
    const response = await api.get<{ totalCount: number }>('/employees/count', { params });
    return response.data.totalCount;
  },

  getById: async (id: string): Promise<Employee> => {
    const response = await api.get<Employee>(`/employees/${id}`);
    return response.data;
  },

  getProfile: async (id: string): Promise<EmployeeProfile> => {
    const response = await api.get<EmployeeProfile>(`/employees/${id}/profile`);
    return response.data;
  },

  getDirectory: async (params?: {
    organizationId?: string;
    branchId?: string;
    departmentId?: string;
    designationId?: string;
    location?: string;
  }): Promise<EmployeeDirectoryItem[]> => {
    const response = await api.get<EmployeeDirectoryItem[]>('/employees/directory', { params });
    return response.data;
  },

  createQuick: async (employee: CreateEmployeeQuick): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/employees/quick-create', employee);
    return response.data;
  },

  bulkUploadEmployees: async (rows: BulkUploadEmployeeRow[], tenantId: string): Promise<BulkUploadEmployeesResponse> => {
    const response = await api.post<BulkUploadEmployeesResponse>('/employees/bulk-upload', { rows, tenantId });
    return response.data;
  },

  getPendingCredentialEmployees: async (): Promise<BulkUploadedEmployeeInfo[]> => {
    const response = await api.get<BulkUploadedEmployeeInfo[]>('/employees/pending-credentials');
    return response.data;
  },

  create: async (employee: any): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/employees', employee);
    return response.data;
  },

  update: async (id: string, employee: Partial<Employee>): Promise<void> => {
    await api.put(`/employees/${id}`, { ...employee, id });
  },

  updateBasicDetails: async (id: string, details: UpdateEmployeeBasicDetails): Promise<void> => {
    await api.put(`/employees/${id}/basic`, details);
  },

  updateLoginDetails: async (id: string, email: string): Promise<void> => {
    await api.put(`/employees/${id}/login`, { id, email });
  },

  changePassword: async (id: string, newPassword: string): Promise<void> => {
    await api.put(`/employees/${id}/password`, { employeeId: id, newPassword });
  },

  updateBiometricMapping: async (id: string, biometricUserId: string): Promise<void> => {
    await api.put(`/employees/${id}/biometric`, { employeeId: id, biometricUserId });
  },

  updateJobDetails: async (id: string, details: UpdateEmployeeJobDetails): Promise<void> => {
    await api.put(`/employees/${id}/job`, details);
  },

  updateBankDetails: async (id: string, details: UpdateEmployeeBankDetails): Promise<void> => {
    await api.put(`/employees/${id}/bank-details`, details);
  },

  updateContactDetails: async (id: string, details: UpdateEmployeeContactDetails): Promise<void> => {
    await api.put(`/employees/${id}/contact`, details);
  },

  updateSupervisor: async (id: string, data: UpdateEmployeeSupervisor): Promise<void> => {
    await api.put(`/employees/${id}/supervisor`, data);
  },

  updateSettings: async (id: string, data: UpdateEmployeeSettings): Promise<void> => {
    await api.put(`/employees/${id}/settings`, data);
  },

  updateRoles: async (id: string, data: UpdateEmployeeRoles): Promise<void> => {
    await api.put(`/employees/${id}/roles`, data);
  },

  uploadDocument: async (id: string, file: File, data: Omit<UploadEmployeeDocument, 'employeeId' | 'filePath' | 'fileSize'>): Promise<{ id: string; filePath: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', data.documentType);
    if (data.documentName) formData.append('documentName', data.documentName);
    if (data.documentNumber) formData.append('documentNumber', data.documentNumber);
    if (data.issueDate) formData.append('issueDate', data.issueDate);
    if (data.expiryDate) formData.append('expiryDate', data.expiryDate);
    if (data.remarks) formData.append('remarks', data.remarks);
    const response = await api.post<{ id: string; filePath: string }>(`/employees/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getDocumentDownloadUrl: async (docId: string): Promise<{ url: string }> => {
    const response = await api.get<{ url: string }>(`/employees/documents/${docId}/download-url`);
    return response.data;
  },

  uploadProfileImage: async (id: string, file: File): Promise<{ profileImagePath: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ profileImagePath: string }>(`/employees/${id}/profile-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getProfileImageUrl: async (id: string): Promise<{ url: string }> => {
    const response = await api.get<{ url: string }>(`/employees/${id}/profile-image`);
    return response.data;
  },

  deleteProfileImage: async (id: string): Promise<{ message: string; blobDeleted: boolean }> => {
    const response = await api.delete<{ message: string; blobDeleted: boolean }>(`/employees/${id}/profile-image`);
    return response.data;
  },

  // Emergency Contacts
  addEmergencyContact: async (id: string, data: AddEmergencyContact): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`/employees/${id}/emergency-contacts`, data);
    return response.data;
  },

  deleteEmergencyContact: async (contactId: string): Promise<void> => {
    await api.delete(`/employees/emergency-contacts/${contactId}`);
  },

  updateEmergencyContact: async (contactId: string, data: UpdateEmergencyContact): Promise<void> => {
    await api.put(`/employees/emergency-contacts/${contactId}`, data);
  },

  // Dependents
  addDependent: async (id: string, data: AddDependent): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`/employees/${id}/dependents`, data);
    return response.data;
  },

  deleteDependent: async (dependentId: string): Promise<void> => {
    await api.delete(`/employees/dependents/${dependentId}`);
  },

  updateDependent: async (dependentId: string, data: UpdateDependent): Promise<void> => {
    await api.put(`/employees/dependents/${dependentId}`, data);
  },

  // Email Subscriptions
  updateEmailSubscriptions: async (id: string, data: UpdateEmployeeEmailSubscriptions): Promise<void> => {
    await api.put(`/employees/${id}/email-subscriptions`, data);
  },

  // Subscriber Groups
  addSubscriberGroup: async (id: string, data: AddSubscriberGroup): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`/employees/${id}/subscriber-groups`, data);
    return response.data;
  },

  deleteSubscriberGroup: async (groupId: string): Promise<void> => {
    await api.delete(`/employees/subscriber-groups/${groupId}`);
  },

  updateSubscriberGroup: async (groupId: string, data: UpdateSubscriberGroup): Promise<void> => {
    await api.put(`/employees/subscriber-groups/${groupId}`, data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/employees/${id}`);
  },

  sendLoginInstruction: async (id: string, email?: string): Promise<{ emailSent: boolean; whatsAppSent: boolean; errorMessage?: string }> => {
    const response = await api.post(`/employees/${id}/send-login-instruction`, { email: email || null });
    return response.data;
  },

  exportEmployees: async (searchTerm?: string, sections?: { basicDetails?: boolean; contactDetails?: boolean; jobDetails?: boolean; supervisorMapping?: boolean; employeeSettings?: boolean }): Promise<void> => {
    const params: any = {};
    if (searchTerm) params.searchTerm = searchTerm;
    if (sections) {
      if (sections.basicDetails !== undefined) params.basicDetails = sections.basicDetails;
      if (sections.contactDetails !== undefined) params.contactDetails = sections.contactDetails;
      if (sections.jobDetails !== undefined) params.jobDetails = sections.jobDetails;
      if (sections.supervisorMapping !== undefined) params.supervisorMapping = sections.supervisorMapping;
      if (sections.employeeSettings !== undefined) params.employeeSettings = sections.employeeSettings;
    }
    const response = await api.get('/employees/export', {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const today = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `Employee_Export_${today}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  getLastEmployeeCode: async (): Promise<{ employeeCode: string | null }> => {
    const response = await api.get<{ employeeCode: string | null }>('/employees/last-code');
    return response.data;
  },
};
